import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { Plus, Trash2, Play, Edit2, Power } from 'lucide-react';

const Scenarios = () => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [executing, setExecuting] = useState({});

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      const response = await api.get('/scenarios');
      setScenarios(response.data);
    } catch (error) {
      console.error('Error loading scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeScenario = async (scenarioId) => {
    setExecuting({ ...executing, [scenarioId]: true });
    try {
      await api.post(`/scenarios/${scenarioId}/execute`);
      alert('✅ Scénario exécuté avec succès !');
    } catch (error) {
      console.error('Error executing scenario:', error);
      alert('❌ Erreur lors de l\'exécution du scénario');
    } finally {
      setTimeout(() => {
        setExecuting({ ...executing, [scenarioId]: false });
      }, 1000);
    }
  };

  const toggleScenario = async (scenarioId, isActive) => {
    try {
      await api.put(`/scenarios/${scenarioId}`, {
        is_active: !isActive,
      });
      loadScenarios();
    } catch (error) {
      console.error('Error toggling scenario:', error);
    }
  };

  const deleteScenario = async (scenarioId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce scénario ?')) {
      return;
    }
    try {
      await api.delete(`/scenarios/${scenarioId}`);
      loadScenarios();
    } catch (error) {
      console.error('Error deleting scenario:', error);
    }
  };

  // Predefined scenarios examples
  const predefinedScenarios = [
    {
      name: '🌅 Réveil Doux',
      description: 'Allume progressivement les lumières et le chauffage',
      trigger: { type: 'time', value: '07:00' },
      actions: [
        { type: 'lights', command: 'turn_on', parameters: { brightness: 30 } },
        { type: 'heaters', command: 'set_temperature', parameters: { temperature: 21 } },
      ],
    },
    {
      name: '🔥 Montée Rapide',
      description: 'Active tous les systèmes de chauffage',
      trigger: { type: 'manual' },
      actions: [
        { type: 'boiler', command: 'turn_on' },
        { type: 'heaters', command: 'turn_on' },
        { type: 'pellet_stove', command: 'turn_on' },
      ],
    },
    {
      name: '🌑 Mode Nuit',
      description: 'Éteint toutes les lumières et réduit le chauffage',
      trigger: { type: 'time', value: '23:00' },
      actions: [
        { type: 'lights', command: 'turn_off' },
        { type: 'heaters', command: 'set_temperature', parameters: { temperature: 18 } },
      ],
    },
    {
      name: '🌿 Mode Éco',
      description: 'Optimise la consommation énergétique',
      trigger: { type: 'condition', value: 'solar_production > consumption' },
      actions: [
        { type: 'heaters', command: 'turn_on' },
        { type: 'consumption', command: 'optimize' },
      ],
    },
  ];

  const createPredefinedScenario = async (scenario) => {
    try {
      await api.post('/scenarios', scenario);
      loadScenarios();
      alert(`✅ Scénario "${scenario.name}" créé !`);
    } catch (error) {
      console.error('Error creating scenario:', error);
    }
  };

  return (
    <Layout title="Scénarios">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Scénarios Automatiques</h2>
            <p className="text-gray-400 mt-1">
              {scenarios.length} scénario{scenarios.length > 1 ? 's' : ''} configuré{scenarios.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Predefined Scenarios */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <h3 className="text-xl font-bold mb-4">✨ Scénarios Prédéfinis</h3>
          <p className="text-indigo-100 mb-4">
            Cliquez sur un scénario pour le créer et l'ajouter à votre liste :
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {predefinedScenarios.map((scenario, index) => (
              <button
                key={index}
                onClick={() => createPredefinedScenario(scenario)}
                className="text-left p-4 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all"
                data-testid={`predefined-scenario-${index}`}
              >
                <h4 className="font-semibold mb-1">{scenario.name}</h4>
                <p className="text-sm text-indigo-100">{scenario.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* User Scenarios */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">Chargement...</div>
          </div>
        ) : scenarios.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
            <Play className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Aucun scénario créé</h3>
            <p className="text-gray-400 mb-6">
              Créez votre premier scénario en utilisant un modèle prédéfini ci-dessus.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className={`bg-gray-800 rounded-xl p-6 border-2 shadow-lg transition-all ${
                  scenario.is_active ? 'border-green-600' : 'border-gray-700'
                }`}
                data-testid={`scenario-card-${scenario.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-white">{scenario.name}</h3>
                      {scenario.is_active ? (
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                          ✓ Actif
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-gray-600 text-gray-300 text-xs font-medium rounded-full">
                          Inactif
                        </span>
                      )}
                    </div>
                    {scenario.description && (
                      <p className="text-gray-400 mb-3">{scenario.description}</p>
                    )}
                    <div className="text-sm text-gray-500">
                      {scenario.actions.length} action{scenario.actions.length > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => executeScenario(scenario.id)}
                      disabled={executing[scenario.id] || !scenario.is_active}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                      data-testid={`execute-scenario-${scenario.id}`}
                    >
                      <Play className="w-4 h-4" />
                      <span>{executing[scenario.id] ? 'Exécution...' : 'Exécuter'}</span>
                    </button>
                    <button
                      onClick={() => toggleScenario(scenario.id, scenario.is_active)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        scenario.is_active
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                      data-testid={`toggle-scenario-${scenario.id}`}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteScenario(scenario.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      data-testid={`delete-scenario-${scenario.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-900 bg-opacity-50 border border-blue-700 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-blue-200 mb-2">🧠 Scénarios Intelligents</h4>
          <p className="text-blue-100 text-sm">
            Les scénarios vous permettent d'automatiser vos actions domotiques.
            Créez des automatisations basées sur l'heure, la température, ou la production solaire.
            Les scénarios peuvent être exécutés manuellement ou automatiquement selon leurs déclencheurs.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Scenarios;